// libraries
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import Dinero from 'dinero.js'
import { useDispatch, useSelector } from 'react-redux'
import { useForm, useFormContext, FormProvider } from 'react-hook-form'
import {
  Box,
  InputAdornment,
  Grid,
  Stack,
  IconButton,
  Button,
  Typography,
  FormHelperText,
  Tooltip,
} from '@mui/material'
import { useTheme, makeStyles } from '@mui/styles'
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material'
import { randomId } from '@mui/x-data-grid-generator'
import { useTranslation } from 'react-i18next'
import HistoryIcon from '@mui/icons-material/History'

// state
import { removeScrapVehicle, resetScrapVehiclesEdited } from 'state/inboundTicket'
// specific components
import ScaleImageCell from 'components/containers/inbound-tickets/components/ScaleImageCell'
import MultiImageBlade from 'components/generic/modals/blade-modal/MultiImageBlade'
import MaterialEntryFormTruckWeights from 'components/containers/inbound-tickets/components/MaterialEntryFormTruckWeights'
import PurchasePriceHistoryBlade from 'components/generic/modals/blade-modal/PurchasePriceHistoryBlade'

// generic components
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'
import Input from 'components/generic/form/Input'
import MaterialAutocomplete from 'components/generic/form/AsyncAutocomplete/MaterialAutocomplete'
import AdjustmentModal from 'components/generic/modals/dialog-modal/AdjustmentModal'
import ScalePopover from 'components/generic/popovers/ScalePopover'
import AxleWeightsModal from 'components/containers/inbound-tickets/components/AxleWeightsModal'

// helpers and utils
import { useScaleState } from 'utils/custom-hooks/useScaleState'
import { getNetValue } from 'utils/data-manipulation/common'
import { generateRequestImagesFunction } from 'utils/images'
import {
  SCALE_MATERIAL_VIEW_OPTIONS,
  MEASURE_UNIT,
  DEFAULT_PRECISION,
  IMAGE_TYPES,
  DEFAULT_AXLE_DATA,
  GS_ROLE,
} from 'utils/constants'
import { setDecimalPrecision, calcAdjustments, convertUM } from 'utils/helpers'
import { roundTo } from 'utils/data-manipulation/valueFormatter'
import {
  priceValidationInfo,
  isPriceOutsideMinMax,
} from 'components/containers/inbound-tickets/helpers/price-validation'
import {
  INPUT_FOCUS_INDEX,
  onKeyPressNumberField,
  formatAndSavePrice,
  buttonDisabled,
  getInputButtons,
  onClickEntryButton,
  onPressTab,
} from 'components/containers/inbound-tickets/helpers/materialEntryFormHelpers'
import { isManualValidationInfo } from 'components/containers/inbound-tickets/helpers/grid-validations'
import { formatAxleWeightsFromFormData } from 'components/containers/inbound-tickets/helpers/axleWeightsHelpers'
import { isTransferFromInboundTicketData } from '../utils.js'

const useStyles = isAdmin =>
  makeStyles(theme => {
    const { warning, error } = theme.palette
    const color = isAdmin ? warning.main : error.main
    return {
      root: {
        '& .MuiInputLabel-root.Mui-error': { color },
        '& .MuiFormHelperText-root': {
          color,
          marginLeft: 0,
        },
        '& .Mui-error .MuiOutlinedInput-notchedOutline.MuiOutlinedInput-notchedOutline': {
          borderColor: color,
        },
      },
    }
  })

const MaterialEntryForm = ({
  selectedMaterial,
  materialEntryViewMode,
  updateMaterialEntryViewMode,
  updateMaterialRow,
  deleteMaterialRow,
  pricePrepend,
  updateTruckWeight,
  truckWeight,
  purchaseOrderData,
  onEditScrapVehicle,
  clearErrors,
  activeMaterialForAxleWeights,
  setActiveMaterialForAxleWeights,
  materials,
  inboundTicketData,
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation('inboundTicket')
  const theme = useTheme()

  const scaleContainerMethods = useFormContext()
  const methods = useForm()
  const { setValue, getValues, control, watch } = methods
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const lastTareWeight = parseInt(materials[materials.length - 1]?.weightAndPrice?.tare?.amount) || 0

  const isTruck = materialEntryViewMode === SCALE_MATERIAL_VIEW_OPTIONS.TRUCK_ENTRY
  const isTransfer = isTransferFromInboundTicketData(inboundTicketData)

  const { selectedCameraIds, selectedScaleIds, scaleCameras } = useSelector(state => state.devices)
  const { customer: supplier } = useSelector(state => state.customer)
  const { roles, isAdmin } = useSelector(state => state.permissions)
  const classes = useStyles(isAdmin)()
  const hasEditPermission = !!roles?.find(
    r =>
      r.roleName === GS_ROLE.YARD_ADMIN || r.roleName === GS_ROLE.SCALE_OPERATOR || r.roleName === GS_ROLE.YARD_MANAGER,
  )
  const { defaultYardUm, settings } = useSelector(state => state.settings)
  const { showWeightDecimals, flagManuallyEnteredWeights, enableAxleWeights, defaultNumberOfAxles } =
    settings?.purchaseSettings?.inboundTickets || {}
  const { defaultCurrency } = useSelector(state => state.settings)
  const [isMissingMaterial, setIsMissingMaterial] = useState(false)
  const [priceFieldValidationInfo, setPriceFieldValidationInfo] = useState({})
  const [unitOfMeasure, setUnitOfMeasure] = useState(null)
  const [customerPreferredUm, setCustomerPreferredUm] = useState(unitOfMeasure)
  const [openDeduction, setOpenDeduction] = useState(false)
  const [scalePopoverData, setScalePopoverData] = useState({})
  const [selectedDeductions, setSelectedDeductions] = useState([])
  const [inputFocusIndex, setInputFocusIndex] = useState(INPUT_FOCUS_INDEX.GROSS)
  const [grossScaleButtonClicked, setGrossScaleButtonClicked] = useState(false)
  const [tareScaleButtonClicked, setTareScaleButtonClicked] = useState(false)
  const [selectedPriceEntry, setSelectedPriceEntry] = useState(null)
  const [openPurchasePriceHistoryBlade, setOpenPurchasePriceHistoryBlade] = useState(false)
  const [customerMaterialPair, setCustomerMaterialPair] = useState({})

  const showPriceHistoryButton = customerMaterialPair.customerId && customerMaterialPair.materialTypeId
  useEffect(() => setCustomerMaterialPair({ ...customerMaterialPair, customerId: supplier.id }), [supplier])

  const materialNameEl = useRef(null)
  const materialGrossEl = useRef(null)
  const materialTareEl = useRef(null)
  const materialPriceEl = useRef(null)

  const isNativeCamera =
    selectedCameraIds?.length === 0 && Object.values(scaleCameras || {}).every(cameras => !cameras?.length)
  const [multiImageBladeData, setMultiImageBladeData] = useState(null)
  const { requestImages } = generateRequestImagesFunction()

  const { isFetchingScaleReading, showScaleButton, readScale } = useScaleState()

  const { canAutoSelect, canFlagManualScaleReadOverrideFF, enableTransferTicketsConsumerWeightsFF } = useSelector(
    state => state.featureFlags?.variables || {},
  )
  const canFlagManualScaleReadOverride = canFlagManualScaleReadOverrideFF && flagManuallyEnteredWeights

  const isScrapVehicle = selectedMaterial?.materialCode === 'scrap-vehicle'
  const materialGrossImages = watch('material.grossImages')
  const materialTareImages = watch('material.tareImages')

  const { transportationInformation } = useSelector(state => state.inboundTicket)
  const { scrapVehiclesWereEdited } = useSelector(state => state.inboundTicket.transportationInformation)
  const purchaseOrderId = transportationInformation?.purchaseOrderId || ''

  const clearForm = () => {
    setValue('material.name', '')
    setValue('material.grossImages', [])
    setValue('material.tareImages', [])
  }

  const handleDeleteClick = () => {
    if (materialEntryViewMode === SCALE_MATERIAL_VIEW_OPTIONS.MATERIAL_ENTRY_UPDATE) {
      deleteMaterialRow(selectedMaterial.id)
      if (isScrapVehicle) {
        dispatch(removeScrapVehicle(selectedMaterial.id))
      }
      clearForm()
      setValue('material.gross', '')
      setValue('material.tare', '')
      setValue('material.price', undefined)
      setValue('material.isManualGross', undefined)
      setValue('material.isManualTare', undefined)
      setSelectedPriceEntry(null)
    }

    if (materialEntryViewMode === SCALE_MATERIAL_VIEW_OPTIONS.MATERIAL_ENTRY_NEW) {
      clearForm()
      setValue('material.isManualGross', undefined)
      setValue('material.isManualTare', undefined)
    }

    updateMaterialEntryViewMode(SCALE_MATERIAL_VIEW_OPTIONS.BUTTONS)
    setShowDeleteModal(false)
  }

  const getUpdatedMaterialValues = () => {
    const name = getValues('material.name') || ''
    const gross = parseFloat(getValues('material.gross') || 0).toFixed(showWeightDecimals ? 1 : 0)
    const tareValue = getValues('material.tare')
    const tare =
      tareValue === null || tareValue === undefined || tareValue === ''
        ? null
        : parseFloat(tareValue || 0).toFixed(showWeightDecimals ? 1 : 0)
    const price = setDecimalPrecision(Number(getValues('material.price')) || 0, { shouldFormatNumber: false }) || 0
    const basePrice =
      setDecimalPrecision(
        convertUM(getValues('material.price'), unitOfMeasure?.toLowerCase(), customerPreferredUm?.toLowerCase()),
        {
          shouldFormatNumber: false,
          decimalPrecisionOverride: 4,
        },
      ) || 0
    const isManualGross =
      typeof getValues('material.isManualGross') === 'boolean' ? getValues('material.isManualGross') : undefined
    const isManualTare =
      typeof getValues('material.isManualTare') === 'boolean' ? getValues('material.isManualTare') : undefined
    const axleWeights = getValues('material.axleWeights') || []
    return {
      name,
      gross,
      tare,
      price,
      basePrice,
      grossImages: materialGrossImages || [],
      tareImages: materialTareImages || [],
      isManualGross,
      isManualTare,
      axleWeights,
    }
  }

  const updateSelectedMaterial = ({
    gross,
    tare,
    price,
    basePrice,
    grossImages,
    tareImages,
    isManualGross,
    isManualTare,
    axleWeights,
  }) => {
    const value = getNetValue({
      gross,
      tare,
      price: basePrice,
      deductions: selectedDeductions,
      fromUnit: defaultYardUm,
      toUnit: unitOfMeasure || defaultYardUm,
    })
    const updatedMaterial = {
      ...selectedMaterial,
      axleWeights,
      weightAndPrice: {
        ...selectedMaterial?.weightAndPrice,
        um: unitOfMeasure,
        customerPreferredUm,
        isManualGross,
        isManualTare,
        gross: {
          ...selectedMaterial?.weightAndPrice?.gross,
          amount: gross,
        },
        tare:
          selectedMaterial?.materialType?.tareWeightRequired && tare === null
            ? null
            : {
                ...selectedMaterial?.weightAndPrice?.tare,
                amount: tare === null ? 0 : tare,
              },
        netWeight: {
          units: selectedMaterial?.weightAndPrice?.gross?.units,
          amount:
            (Number(gross) * 10 -
              Number(tare) * 10 -
              calcAdjustments({
                weightAndPrice: {
                  gross: { amount: Number(gross) },
                  tare: { amount: Number(tare) },
                  deductions: selectedDeductions,
                },
              }).weightAdjustments *
                10) /
            10,
        },
        deductions: selectedDeductions,
        netPrice: {
          ...selectedMaterial?.weightAndPrice?.netPrice,
          amount: roundTo(basePrice * 10 ** selectedMaterial?.weightAndPrice.netPrice?.precision),
        },
        customerPreferredPrice: {
          ...selectedMaterial?.weightAndPrice?.customerPreferredPrice,
          amount: roundTo(price * 10 ** selectedMaterial?.weightAndPrice.customerPreferredPrice?.precision),
        },
        netValue: {
          ...selectedMaterial?.weightAndPrice?.netValue,
          amount: roundTo(value * 10 ** selectedMaterial?.weightAndPrice.netValue?.precision),
        },
      },
      status: {
        ...selectedMaterial.status,
        grossImages,
        tareImages,
      },
    }
    if (selectedPriceEntry?.materialTypeId) {
      updatedMaterial.materialCode = selectedPriceEntry?.materialCode
      updatedMaterial.materialName = selectedPriceEntry?.materialName
      updatedMaterial.materialTypeId = selectedPriceEntry?.materialTypeId
      updatedMaterial.orderEntryId = selectedPriceEntry?.orderEntryId
      updatedMaterial.overrideName = selectedPriceEntry?.overrideName
      updatedMaterial.materialType = {
        code: selectedPriceEntry?.materialCode,
        commodityId: selectedPriceEntry?.commodityId,
        commodityName: selectedPriceEntry?.commodityName,
        commonName: selectedPriceEntry?.materialName,
        id: selectedPriceEntry?.materialTypeId,
        rules: selectedPriceEntry?.rules,
        tareWeightRequired: selectedPriceEntry?.tareWeightRequired,
      }
    }
    updateMaterialRow(updatedMaterial)
  }

  const commitNewMaterial = ({
    gross,
    tare,
    price,
    grossImages,
    tareImages,
    isManualGross,
    isManualTare,
    axleWeights,
  }) => {
    const value = getNetValue({
      gross,
      tare,
      price,
      deductions: selectedDeductions,
      fromUnit: defaultYardUm,
      toUnit: unitOfMeasure,
    })
    const material = {
      id: randomId(),
      materialName: selectedPriceEntry?.materialName,
      materialCode: selectedPriceEntry?.materialCode,
      materialTypeId: selectedPriceEntry?.materialTypeId,
      orderEntryId: selectedPriceEntry?.orderEntryId,
      overrideName: selectedPriceEntry?.overrideName,
      materialType: {
        rules: selectedPriceEntry?.rules,
        tareWeightRequired: selectedPriceEntry?.tareWeightRequired,
      },
      axleWeights,
      weightAndPrice: {
        isManualGross,
        isManualTare,
        gross: {
          amount: gross,
          units: defaultYardUm,
        },
        tare:
          selectedPriceEntry?.tareWeightRequired && tare === null
            ? null
            : {
                amount: tare === null ? 0 : tare,
                units: defaultYardUm,
              },
        netWeight: {
          amount:
            (Number(gross) * 10 -
              Number(tare) * 10 -
              calcAdjustments({
                weightAndPrice: {
                  gross: { amount: Number(gross) },
                  tare: { amount: Number(tare) },
                  deductions: selectedDeductions,
                },
              }).weightAdjustments *
                10) /
            10,
          units: defaultYardUm,
        },
        deductions: selectedDeductions,
        um: unitOfMeasure,
        customerPreferredUm: unitOfMeasure,
        netPrice: {
          amount: roundTo(price * 10 ** (selectedPriceEntry.buyPrice?.price?.precision || DEFAULT_PRECISION)),
          currency: defaultCurrency,
          precision: selectedPriceEntry.buyPrice?.price?.precision || DEFAULT_PRECISION,
        },
        customerPreferredPrice: {
          amount: roundTo(price * 10 ** (selectedPriceEntry.buyPrice?.price?.precision || DEFAULT_PRECISION)),
          currency: defaultCurrency,
          precision: selectedPriceEntry.buyPrice?.price?.precision || DEFAULT_PRECISION,
        },
        netValue: {
          amount: roundTo(value * 10 ** (selectedPriceEntry.buyPrice?.price?.precision || DEFAULT_PRECISION)),
          currency: defaultCurrency,
          precision: selectedPriceEntry.buyPrice?.price?.precision || DEFAULT_PRECISION,
        },
      },
      status: {
        grossImages,
        tareImages,
      },
    }
    updateMaterialRow(material)
  }

  const onDone = () => {
    if (isTruck) {
      const truckFormData = getValues('truck')
      updateTruckWeight({
        isManualGross: truckFormData?.isManualGross,
        isManualTare: truckFormData?.isManualTare,
        gross: {
          amount: truckFormData?.gross || 0,
          units: defaultYardUm,
          grossImages: truckFormData?.grossImages || [],
        },
        tare: {
          amount: truckFormData?.tare || 0,
          units: defaultYardUm,
          tareImages: truckFormData?.tareImages || [],
        },
        axleWeights: truckFormData.axleWeights || [],
      })
    } else {
      if (!getValues('material.name') && !isScrapVehicle) {
        setIsMissingMaterial(true)
        return
      }
      if (!isScrapVehicle) {
        if (materialEntryViewMode === SCALE_MATERIAL_VIEW_OPTIONS.MATERIAL_ENTRY_UPDATE) {
          updateSelectedMaterial(getUpdatedMaterialValues())
        } else {
          commitNewMaterial(getUpdatedMaterialValues())
        }

        clearForm()
        setValue('material.gross', '')
        setValue('material.tare', '')
        setValue('material.price', undefined)
        setValue('material.isManualGross', false)
        setValue('material.isManualTare', false)
        setValue('material.axleWeights', [])
        setSelectedPriceEntry(null)
        setCustomerPreferredUm(null)
      }
    }
    if (canFlagManualScaleReadOverride) {
      setGrossScaleButtonClicked(false)
      setTareScaleButtonClicked(false)
    }
    updateMaterialEntryViewMode(SCALE_MATERIAL_VIEW_OPTIONS.BUTTONS)
    clearErrors('materialGrid')
  }

  const formattedPriceUnit = (customerPreferredUm || unitOfMeasure || defaultYardUm)?.toUpperCase()

  const priceIsValid =
    isAdmin ||
    !isPriceOutsideMinMax(
      getValues('material.price'),
      Dinero({ ...selectedPriceEntry?.buyPrice?.price }).toUnit(),
      selectedPriceEntry?.buyPrice?.minMaxRange,
    )

  const onSaveDeduction = useCallback(deductions => {
    setOpenDeduction(false)
    setSelectedDeductions(deductions)
  }, [])

  const callReadScale = ({ weightType, scaleId = '' }) => {
    readScale(scaleData => {
      const isGross = weightType === 'gross'
      const inputEl = isGross ? materialGrossEl : materialTareEl

      setValue(`material.${weightType}`, scaleData.weight)
      inputEl?.current?.focus()
      setInputFocusIndex(INPUT_FOCUS_INDEX[isGross ? 'GROSS' : 'TARE'])
      if (canFlagManualScaleReadOverride) {
        if (isGross) setGrossScaleButtonClicked(true)
        else setTareScaleButtonClicked(true)
      }
    }, scaleId)
  }

  const scaleButtonClickHandler = (weightType, event) => {
    if (!['gross', 'tare'].includes(weightType)) throw new Error('Invalid weight type!')

    if (selectedScaleIds?.length > 1) {
      setScalePopoverData({
        anchorEl: event.target,
        weightType,
      })
    } else {
      callReadScale({ weightType })
      if (weightType === 'gross') {
        callRequestImages(materialGrossImages, 'material.grossImages', selectedScaleIds[0])
      } else {
        callRequestImages(materialTareImages, 'material.tareImages', selectedScaleIds[0])
      }
    }
  }

  const isManualGross = getValues('material.isManualGross')
  const isManualTare = getValues('material.isManualTare')
  const materialFieldList = [
    {
      index: INPUT_FOCUS_INDEX.NAME,
      ref: materialNameEl,
      fieldName: 'material.name',
    },
    {
      index: INPUT_FOCUS_INDEX.GROSS,
      ref: materialGrossEl,
      fieldName: 'material.gross',
    },
    {
      index: INPUT_FOCUS_INDEX.TARE,
      ref: materialTareEl,
      fieldName: 'material.tare',
    },
    {
      index: INPUT_FOCUS_INDEX.PRICE,
      ref: materialPriceEl,
      fieldName: 'material.price',
    },
  ]

  const validateIsManual = useCallback(
    (valueUpdated, field) => {
      const isGross = field === 'gross'
      const valueFormatted = setDecimalPrecision(valueUpdated, {
        removeTrailingZeros: false,
        decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
      })
      const scaleButtonClicked = isGross ? grossScaleButtonClicked : tareScaleButtonClicked

      if (valueUpdated === undefined) return null

      // Reset of ...ScaleButtonClicked
      if (scaleButtonClicked) {
        if (isGross) setGrossScaleButtonClicked(false)
        else setTareScaleButtonClicked(false)
        return false
      }

      if (materialEntryViewMode === SCALE_MATERIAL_VIEW_OPTIONS.MATERIAL_ENTRY_NEW) {
        if (valueUpdated === '') return false
        return true
      }

      if (materialEntryViewMode === SCALE_MATERIAL_VIEW_OPTIONS.MATERIAL_ENTRY_UPDATE) {
        const selectedValue = selectedMaterial?.weightAndPrice?.[isGross ? 'gross' : 'tare']?.amount
        const selectedValueIsManual = selectedMaterial?.weightAndPrice?.[isGross ? 'isManualGross' : 'isManualTare']
        const selectedValueFormatted = setDecimalPrecision(parseInt(selectedValue, 10), {
          removeTrailingZeros: false,
          decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
        })
        if (valueFormatted === selectedValueFormatted) {
          return selectedValueIsManual
        }
      }

      if (materialEntryViewMode === SCALE_MATERIAL_VIEW_OPTIONS.TRUCK_ENTRY) {
        if (valueUpdated === null) return false

        const selectedValue = truckWeight?.[isGross ? 'gross' : 'tare']?.amount
        const selectedValueIsManual = truckWeight?.[isGross ? 'isManualGross' : 'isManualTare']
        const selectedValueFormatted = setDecimalPrecision(parseInt(selectedValue, 10), {
          removeTrailingZeros: false,
          decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
        })
        if (valueFormatted === selectedValueFormatted) {
          return selectedValueIsManual
        }
      }

      return true
    },
    [
      showWeightDecimals,
      grossScaleButtonClicked,
      tareScaleButtonClicked,
      materialEntryViewMode,
      selectedMaterial,
      truckWeight,
    ],
  )

  const currentGross = watch('material.gross')
  const [grossFieldColor, setGrossFieldColor] = useState(null)

  useEffect(() => {
    if (canFlagManualScaleReadOverride) {
      const isManual = validateIsManual(currentGross, 'gross')
      const { color } = isManualValidationInfo(isManual, canFlagManualScaleReadOverride)
      setGrossFieldColor(color)
      setValue('material.isManualGross', isManual)
    }
  }, [currentGross])

  const currentTare = watch('material.tare')
  const [tareFieldColor, setTareFieldColor] = useState(null)

  useEffect(() => {
    if (canFlagManualScaleReadOverride) {
      const isManual = validateIsManual(currentTare, 'tare')
      const { color } = isManualValidationInfo(isManual, canFlagManualScaleReadOverride)
      setTareFieldColor(color)
      setValue('material.isManualTare', isManual)
    }
  }, [currentTare])

  useEffect(() => {
    setIsMissingMaterial(false)
    if (materialEntryViewMode === SCALE_MATERIAL_VIEW_OPTIONS.MATERIAL_ENTRY_NEW && selectedPriceEntry) {
      setInputFocusIndex(hasEditPermission ? INPUT_FOCUS_INDEX.GROSS : INPUT_FOCUS_INDEX.PRICE)
      setValue('material.name', selectedPriceEntry?.materialName)
      setValue('material.gross', getValues('material.gross') || '')
      setValue('material.grossImages', getValues('material.grossImages') || [])
      setValue('material.tare', getValues('material.tare') || '')
      setValue('material.tareImages', getValues('material.tareImages') || [])
      setValue(
        'material.price',
        setDecimalPrecision(Dinero({ ...selectedPriceEntry?.buyPrice?.price }).toUnit(), {
          shouldFormatNumber: false,
        }) || 0,
      )
      if (enableAxleWeights) {
        setValue(
          'material.axleWeights',
          getValues('material.axleWeights') ||
            [...Array(defaultNumberOfAxles).keys()].map((_, index) => ({
              ...DEFAULT_AXLE_DATA,
              axleNumber: index + 1,
            })),
        )
      } else {
        setValue('material.axleWeights', [])
      }
      setUnitOfMeasure(selectedPriceEntry?.weightUnits?.toLowerCase())
      setCustomerPreferredUm(selectedPriceEntry?.weightUnits?.toLowerCase())
      setSelectedDeductions([])
      if (canFlagManualScaleReadOverride) {
        setValue('material.isManualGross', getValues('material.isManualGross') || undefined)
        setValue('material.isManualTare', getValues('material.isManualTare') || undefined)
      }
    }

    if (materialEntryViewMode === SCALE_MATERIAL_VIEW_OPTIONS.TRUCK_ENTRY && truckWeight) {
      setValue('truck.gross', truckWeight?.gross?.amount)
      setValue('truck.grossImages', truckWeight?.gross?.grossImages)
      setValue('truck.tare', truckWeight?.tare?.amount)
      setValue('truck.tareImages', truckWeight?.tare?.tareImages)
      setValue('truck.axleWeights', scaleContainerMethods.getValues('axleWeights'))
      setInputFocusIndex(INPUT_FOCUS_INDEX.GROSS)
      if (canFlagManualScaleReadOverride) {
        setValue('truck.isManualGross', truckWeight?.isManualGross || undefined)
        setValue('truck.isManualTare', truckWeight?.isManualTare || undefined)
        setGrossScaleButtonClicked(false)
        setTareScaleButtonClicked(false)
      }
    }

    if (materialEntryViewMode === SCALE_MATERIAL_VIEW_OPTIONS.MATERIAL_ENTRY_UPDATE) {
      setInputFocusIndex(hasEditPermission ? INPUT_FOCUS_INDEX.GROSS : INPUT_FOCUS_INDEX.PRICE)
    }
  }, [materialEntryViewMode, selectedPriceEntry, truckWeight, enableAxleWeights, defaultNumberOfAxles])

  useEffect(() => {
    if (materialEntryViewMode === SCALE_MATERIAL_VIEW_OPTIONS.MATERIAL_ENTRY_UPDATE && selectedMaterial) {
      setValue('material.name', selectedMaterial?.materialName || '')
      setValue(
        'material.gross',
        setDecimalPrecision(selectedMaterial?.weightAndPrice?.gross?.amount || '', {
          decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
          shouldFormatNumber: false,
          removeTrailingZeros: true,
        }),
      )
      const tareValue = selectedMaterial?.weightAndPrice?.tare?.amount
      setValue(
        'material.tare',
        tareValue === undefined || tareValue === null
          ? ''
          : setDecimalPrecision(selectedMaterial?.weightAndPrice?.tare?.amount, {
              decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
              shouldFormatNumber: false,
              removeTrailingZeros: true,
            }),
      )
      setValue(
        'material.price',
        setDecimalPrecision(
          Dinero({
            ...(selectedMaterial?.weightAndPrice?.customerPreferredPrice || selectedMaterial?.weightAndPrice?.netPrice),
          }).toUnit(),
          { shouldFormatNumber: false },
        ) || 0,
      )
      if (enableAxleWeights) {
        setValue('material.axleWeights', selectedMaterial?.axleWeights || [])
      } else {
        setValue('material.axleWeights', [])
      }
      setSelectedDeductions(selectedMaterial?.weightAndPrice?.deductions || [])
      setValue('material.grossImages', selectedMaterial?.status?.grossImages || [])
      setValue('material.tareImages', selectedMaterial?.status?.tareImages || [])
      setUnitOfMeasure(selectedMaterial?.weightAndPrice?.um?.toLowerCase())
      setCustomerPreferredUm(
        selectedMaterial?.weightAndPrice?.customerPreferredUm?.toLowerCase() ||
          selectedMaterial?.weightAndPrice?.um?.toLowerCase(),
      )
      if (canFlagManualScaleReadOverride) {
        setValue('material.isManualGross', selectedMaterial?.weightAndPrice?.isManualGross || undefined)
        setValue('material.isManualTare', selectedMaterial?.weightAndPrice?.isManualTare || undefined)
        setGrossScaleButtonClicked(false)
        setTareScaleButtonClicked(false)
      }
    }
  }, [materialEntryViewMode, selectedMaterial, enableAxleWeights])

  const currentPrice = watch('material.price')
  useEffect(() => {
    if (selectedPriceEntry && currentPrice !== '.') {
      const { helperText } = priceValidationInfo({
        materialPrice: currentPrice,
        priceEntry: selectedPriceEntry,
        isAdmin,
        priceType: 'buyPrice',
        customerPreferredUm,
        isTransfer: enableTransferTicketsConsumerWeightsFF ? isTransfer : false,
      })
      setPriceFieldValidationInfo({ helperText })
    }
  }, [currentPrice, selectedPriceEntry])

  const callRequestImages = (images, path, scaleId) => {
    if ((images || []).length === 0 && !isNativeCamera) {
      requestImages(imageData => {
        setValue(path, imageData, { shouldDirty: true })
      }, scaleId)
    }
  }

  useEffect(() => {
    if (scrapVehiclesWereEdited) {
      updateMaterialEntryViewMode(SCALE_MATERIAL_VIEW_OPTIONS.BUTTONS)
      dispatch(resetScrapVehiclesEdited())
    }
  }, [scrapVehiclesWereEdited])

  const removeAxleWeights = () => setValue('material.axleWeights', [])

  const onClickAxleWeightsButton = useCallback(() => {
    setActiveMaterialForAxleWeights(getValues(isTruck ? 'truck' : 'material'))
  }, [isTruck])

  const axleWeights = watch('material.axleWeights')

  const inputButtons = useMemo(() => {
    return getInputButtons(defaultYardUm, enableAxleWeights, (axleWeights || [])?.length > 1)
  }, [defaultYardUm, enableAxleWeights, axleWeights])

  return (
    <FormProvider {...methods}>
      <AlertModal
        open={showDeleteModal}
        modalType="delete"
        title="Confirm Delete"
        question={`Are you sure you want to delete ${selectedMaterial.materialName}?`}
        description="All weights, images, and price changes for this material will be deleted."
        confirmLabel="Confirm Delete"
        cancelLabel="CANCEL"
        onClose={() => setShowDeleteModal(false)}
        onSubmit={() => handleDeleteClick()}
      />
      <AdjustmentModal
        title="Adjustments"
        adjustments={selectedDeductions}
        open={openDeduction}
        onSave={onSaveDeduction}
        onClose={() => setOpenDeduction(false)}
      />
      <PurchasePriceHistoryBlade
        customerMaterialPair={customerMaterialPair}
        openCondition={openPurchasePriceHistoryBlade}
        onCloseFunction={() => setOpenPurchasePriceHistoryBlade(false)}
        useBackendSortAndFilter
      />
      <ScalePopover
        scalePopoverData={scalePopoverData}
        setScalePopoverData={setScalePopoverData}
        callReadScale={callReadScale}
        onScaleSelected={scaleId => {
          const weightType = scalePopoverData.weightType
          if (weightType === 'gross') {
            callRequestImages(materialGrossImages, 'material.grossImages', scaleId)
          } else if (weightType === 'tare') {
            callRequestImages(materialTareImages, 'material.tareImages', scaleId)
          }
        }}
      />
      <MultiImageBlade
        multiImageBladeData={multiImageBladeData}
        setMultiImageBladeData={setMultiImageBladeData}
        generateSaveImagesFunction={imageType => imageData => {
          const formPrefix = isTruck ? 'truck' : 'material'
          const { axleNumber } = multiImageBladeData
          if (axleNumber) {
            const currentAxleWeightsArray = multiImageBladeData?.axleWeights || []
            const axleData = currentAxleWeightsArray?.find(axle => axle.axleNumber === axleNumber)
            const updatedAxleWeights = currentAxleWeightsArray.filter(axle => axle.axleNumber !== axleNumber)
            updatedAxleWeights.push({
              ...axleData,
              [`${imageType}Images`]: imageData,
            })
            if (axleNumber === 1) {
              setValue(`${formPrefix}.${imageType}Images`, imageData, { shouldDirty: true })
            }
            setValue(`${formPrefix}.axleWeights`, updatedAxleWeights)
            setMultiImageBladeData(prevMultiImageBladeData => ({
              ...prevMultiImageBladeData,
              axleWeights: updatedAxleWeights,
              [`${imageType}Images`]: imageData,
            }))
          } else {
            setValue(`${formPrefix}.${imageType}Images`, imageData, { shouldDirty: true })
            setMultiImageBladeData({
              ...getValues(formPrefix),
              ...(isTruck && { id: 'truck-total' }),
            })
          }
        }}
      />
      <AxleWeightsModal
        onClose={() => setActiveMaterialForAxleWeights(null)}
        open={!!activeMaterialForAxleWeights}
        materialData={activeMaterialForAxleWeights}
        onSave={(data, numberOfAxles) => {
          const formPrefix = isTruck ? 'truck' : 'material'

          if (numberOfAxles === 1) {
            setValue(`${formPrefix}.gross`, data.axles?.['axle-1']?.gross || 0, {
              shouldDirty: true,
            })
            setValue(`${formPrefix}.tare`, data.axles?.['axle-1']?.tare || 0)
            setValue(`${formPrefix}.grossImages`, data.axles?.['axle-1']?.grossImages || [])
            setValue(`${formPrefix}.tareImages`, data.axles?.['axle-1']?.tareImages || [])
            setValue(`${formPrefix}.isManualGross`, data.axles?.['axle-1']?.isManualGross || false)
            setValue(`${formPrefix}.isManualTare`, data.axles?.['axle-1']?.isManualTare || false)
            setValue(`${formPrefix}.axleWeights`, [])
          } else {
            const oldAxleWeights = getValues(`${formPrefix}.axleWeights`) || []
            const {
              axleWeights: newAxleWeights,
              aggregateGross,
              aggregateTare,
              aggregateIsManualGross,
              aggregateIsManualTare,
            } = formatAxleWeightsFromFormData(data, numberOfAxles, oldAxleWeights)

            setValue(`${formPrefix}.gross`, aggregateGross, { shouldDirty: true })
            setValue(`${formPrefix}.tare`, aggregateTare)
            setValue(`${formPrefix}.grossImages`, data.axles?.['axle-1']?.grossImages || [])
            setValue(`${formPrefix}.tareImages`, data.axles?.['axle-1']?.tareImages || [])
            setValue(`${formPrefix}.isManualGross`, aggregateIsManualGross)
            setValue(`${formPrefix}.isManualTare`, aggregateIsManualTare)
            setValue(`${formPrefix}.axleWeights`, newAxleWeights)
          }
        }}
      />
      {isTruck ? (
        <MaterialEntryFormTruckWeights
          validateIsManual={validateIsManual}
          inputFocusIndex={inputFocusIndex}
          setInputFocusIndex={setInputFocusIndex}
          onDone={onDone}
          setMultiImageBladeData={setMultiImageBladeData}
          onClickAxleWeightsButton={onClickAxleWeightsButton}
          setGrossScaleButtonClicked={setGrossScaleButtonClicked}
          setTareScaleButtonClicked={setTareScaleButtonClicked}
        />
      ) : (
        <Stack direction={{ xs: 'column', md: 'row' }} mt={2} spacing={3}>
          <Box width={{ xs: '100%', md: '40%' }} data-testid="material-info">
            <Stack spacing={2}>
              <Stack>
                <Stack direction="row" alignItems="center" sx={{ justifyContent: 'space-between' }} spacing={1}>
                  {isScrapVehicle ? (
                    <Typography>{selectedMaterial.materialName}</Typography>
                  ) : (
                    <MaterialAutocomplete
                      name="material.name"
                      label={t('material')}
                      data-testid="material-autofill"
                      control={control}
                      variant="outlined"
                      defaultValue={{
                        commonName: selectedMaterial.materialName,
                        code: selectedMaterial.materialCode,
                        materialTypeId: selectedMaterial.materialTypeId,
                        orderEntryId: selectedMaterial.orderEntryId,
                      }}
                      disabled={isScrapVehicle}
                      onMouseDown={() => setInputFocusIndex(INPUT_FOCUS_INDEX.NAME)}
                      onKeyDown={event =>
                        onPressTab({
                          event,
                          fieldList: materialFieldList,
                          inputFocusIndex,
                          setInputFocusIndex,
                          isAdminOrScaleOp: hasEditPermission,
                        })
                      }
                      useSupplementalPriceSheetIds
                      canOverridePrices
                      canAutoSelect={canAutoSelect}
                      TextFieldProps={{
                        inputRef: materialNameEl,
                      }}
                      onChange={(newMaterial, reason) => {
                        if (reason === 'clear') {
                          setValue('material.name', '')
                          setSelectedPriceEntry(null)
                        } else {
                          const isFirstLoad = reason === undefined
                          setValue('material.name', newMaterial?.commonName || '')
                          const {
                            id: materialTypeId,
                            defaultPriceEntry,
                            supplementalPriceEntry,
                            orderEntryId,
                          } = newMaterial
                          const priceEntry = supplementalPriceEntry || defaultPriceEntry
                          setSelectedPriceEntry({
                            ...priceEntry,
                            materialName: newMaterial.commonName,
                            materialCode: newMaterial.code,
                            materialTypeId,
                            orderEntryId,
                            commodityId: newMaterial.commodityId,
                            commodityName: newMaterial.commodityName,
                            tareWeightRequired: newMaterial.tareWeightRequired,
                            rules: newMaterial.rules,
                          })
                          if (!isFirstLoad) {
                            setValue(
                              'material.price',
                              setDecimalPrecision(
                                Dinero({
                                  ...priceEntry?.buyPrice?.price,
                                }).toUnit(),
                                { shouldFormatNumber: false },
                              ) || 0,
                            )
                            setUnitOfMeasure(priceEntry?.weightUnits?.toLowerCase())
                            setCustomerPreferredUm(priceEntry?.weightUnits?.toLowerCase())
                          }
                          setCustomerMaterialPair({ ...customerMaterialPair, materialTypeId: materialTypeId })
                          materialGrossEl.current.focus()
                        }
                      }}
                      orderData={
                        purchaseOrderId && purchaseOrderId === purchaseOrderData?.id ? purchaseOrderData : null
                      }
                    />
                  )}
                  <Stack direction="row" columnGap={0.25}>
                    {isScrapVehicle && (
                      <IconButton
                        size="large"
                        aria-label="edit scrap vehicle"
                        data-testid="edit-scrap-vehicle-button"
                        onClick={() => onEditScrapVehicle(selectedMaterial.vin)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton
                      color="error"
                      size="large"
                      sx={{
                        fill: 'red',
                      }}
                      aria-label="delete"
                      onClick={() => setShowDeleteModal(true)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
                {isMissingMaterial && <FormHelperText error>Material is required</FormHelperText>}
              </Stack>
              <Stack direction="row" columnGap={1}>
                <Input
                  autoFocus
                  name="material.gross"
                  label={t('gross')}
                  data-testid="material-gross"
                  variant="outlined"
                  defaultValue=""
                  fullWidth
                  color={grossFieldColor}
                  focused={canFlagManualScaleReadOverride && isManualGross}
                  control={control}
                  inputRef={materialGrossEl}
                  disabled={isScrapVehicle || isFetchingScaleReading || !hasEditPermission}
                  onChange={() => {
                    setValue('material.axleWeights', [])
                    callRequestImages(materialGrossImages, 'material.grossImages')
                  }}
                  onMouseDown={() => setInputFocusIndex(INPUT_FOCUS_INDEX.GROSS)}
                  onKeyDown={event =>
                    onPressTab({
                      event,
                      fieldList: materialFieldList,
                      inputFocusIndex,
                      setInputFocusIndex,
                      isAdminOrScaleOp: hasEditPermission,
                    })
                  }
                  inputProps={{ inputMode: 'none' }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        sx={{
                          '*': {
                            color: isScrapVehicle || isFetchingScaleReading ? theme.palette.text.disabled : 'unset',
                          },
                        }}
                        position="end"
                      >
                        {defaultYardUm}
                      </InputAdornment>
                    ),
                    onKeyPress: event => onKeyPressNumberField(event, { showWeightDecimals }),
                  }}
                />
                {lastTareWeight > 0 && (
                  <Tooltip title="Use previous tare weight">
                    <Button
                      name="use-previous-tare"
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setValue('material.gross', parseInt(lastTareWeight))
                        setValue('material.axleWeights', [])
                      }}
                    >
                      {`${lastTareWeight.toLocaleString()} ${materials[materials.length - 1]?.weightAndPrice?.tare?.units}`}
                    </Button>
                  </Tooltip>
                )}
                {showScaleButton && (
                  <Button
                    name="read-scale"
                    variant="outlined"
                    size="small"
                    sx={{
                      width: '64px',
                    }}
                    onClick={event => {
                      setValue('material.axleWeights', [])
                      scaleButtonClickHandler('gross', event)
                    }}
                  >
                    {t('take_weight')}
                  </Button>
                )}
              </Stack>
              <Stack direction="row" columnGap={1}>
                <Input
                  name="material.tare"
                  label={t('tare')}
                  data-testid="material-tare"
                  variant="outlined"
                  defaultValue=""
                  fullWidth
                  color={tareFieldColor}
                  focused={canFlagManualScaleReadOverride && isManualTare}
                  control={control}
                  inputRef={materialTareEl}
                  disabled={isScrapVehicle || isFetchingScaleReading || !hasEditPermission}
                  onChange={() => {
                    setValue('material.axleWeights', [])
                    callRequestImages(materialTareImages, 'material.tareImages')
                  }}
                  onMouseDown={() => setInputFocusIndex(INPUT_FOCUS_INDEX.TARE)}
                  onKeyDown={event =>
                    onPressTab({
                      event,
                      fieldList: materialFieldList,
                      inputFocusIndex,
                      setInputFocusIndex,
                      isAdminOrScaleOp: hasEditPermission,
                    })
                  }
                  inputProps={{ inputMode: 'none' }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        sx={{
                          '*': {
                            color: isScrapVehicle || isFetchingScaleReading ? theme.palette.text.disabled : 'unset',
                          },
                        }}
                        position="end"
                      >
                        {defaultYardUm}
                      </InputAdornment>
                    ),
                    onKeyPress: event => onKeyPressNumberField(event, { showWeightDecimals }),
                  }}
                />
                {showScaleButton ? (
                  <Button
                    name="read-scale"
                    variant="outlined"
                    size="small"
                    sx={{
                      width: '64px',
                    }}
                    onClick={event => {
                      setValue('material.axleWeights', [])
                      scaleButtonClickHandler('tare', event)
                    }}
                  >
                    {t('take_weight')}
                  </Button>
                ) : (
                  ''
                )}
              </Stack>
              <Stack direction="row" columnGap={1}>
                <Input
                  name="material.price"
                  label={t('price_per_unit', { unit: formattedPriceUnit })}
                  data-testid="material-price"
                  variant="outlined"
                  defaultValue=""
                  fullWidth
                  control={control}
                  inputRef={materialPriceEl}
                  error={!!priceFieldValidationInfo?.helperText}
                  helperText={priceFieldValidationInfo?.helperText || ''}
                  className={classes.root}
                  disabled={isScrapVehicle}
                  focused={inputFocusIndex === INPUT_FOCUS_INDEX.PRICE}
                  onMouseDown={() => setInputFocusIndex(INPUT_FOCUS_INDEX.PRICE)}
                  onKeyDown={event =>
                    onPressTab({
                      event,
                      fieldList: materialFieldList,
                      inputFocusIndex,
                      setInputFocusIndex,
                      isAdminOrScaleOp: hasEditPermission,
                    })
                  }
                  inputProps={{ inputMode: 'none' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        sx={{
                          '*': {
                            color: isScrapVehicle ? theme.palette.text.disabled : 'unset',
                          },
                        }}
                        position="start"
                      >
                        {pricePrepend}
                      </InputAdornment>
                    ),
                    onChange: event => formatAndSavePrice(event?.target?.value, setValue),
                  }}
                />
                {showPriceHistoryButton && (
                  <Button
                    name="price-history"
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setOpenPurchasePriceHistoryBlade(true)
                    }}
                  >
                    <HistoryIcon />
                  </Button>
                )}
              </Stack>
              <Button variant="outlined" disabled={isScrapVehicle} onClick={() => setOpenDeduction(true)}>
                {selectedDeductions.length > 0 ? t('edit_adjustments') : t('add_adjustment')}
              </Button>
              {!isScrapVehicle ? (
                <Stack direction="row" sx={{ flex: 2 }} spacing={1}>
                  <ScaleImageCell
                    filename={(materialGrossImages || [])[0]?.imageFilename}
                    imageType={IMAGE_TYPES.GROSS}
                    isNativeCamera={isNativeCamera}
                    hasAxleWeights={getValues('material')?.axleWeights?.length}
                    onRetake={() => {
                      requestImages(imageData => {
                        setValue('material.grossImages', imageData, {
                          shouldDirty: true,
                        })
                      })
                    }}
                    onClick={() => {
                      const materialFormData = getValues('material')
                      const hasAxleWeights = materialFormData?.axleWeights?.length

                      const newMultiImageBladeData = {
                        ...materialFormData,
                      }
                      if (hasAxleWeights) {
                        newMultiImageBladeData.axleNumber = 1
                      }
                      if (materialFormData?.grossImages?.length > 0 || isNativeCamera || hasAxleWeights) {
                        setMultiImageBladeData(newMultiImageBladeData)
                      }
                    }}
                  />
                  <ScaleImageCell
                    filename={(materialTareImages || [])[0]?.imageFilename}
                    imageType={IMAGE_TYPES.TARE}
                    isNativeCamera={isNativeCamera}
                    hasAxleWeights={getValues('material')?.axleWeights?.length}
                    onRetake={() => {
                      requestImages(imageData => {
                        setValue('material.tareImages', imageData, {
                          shouldDirty: true,
                        })
                      })
                    }}
                    onClick={() => {
                      const materialFormData = getValues('material')
                      const hasAxleWeights = materialFormData?.axleWeights?.length

                      const newMultiImageBladeData = {
                        ...materialFormData,
                      }
                      if (hasAxleWeights) {
                        newMultiImageBladeData.axleNumber = 1
                      }
                      if (materialFormData?.tareImages?.length > 0 || isNativeCamera || hasAxleWeights) {
                        setMultiImageBladeData(newMultiImageBladeData)
                      }
                    }}
                  />
                </Stack>
              ) : null}
            </Stack>
          </Box>
          <Box width={{ xs: '100%', md: '60%' }} data-testid="input-numberpad">
            <Grid container spacing={1} sx={{ textAlign: 'center', flex: 1 }}>
              {inputButtons.map(button => (
                <Grid key={button.id} item xs={button.id === 'buttonDot' || button.id === 'buttonHyphen' ? 1.5 : 3}>
                  <Button
                    variant={button.id === 'buttonDone' ? 'contained' : 'default'}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: 1,
                      borderColor: 'grey.400',
                      borderRadius: 1,
                      color: 'text.primary',
                      backgroundColor: button.id === 'buttonDone' ? 'primary.main' : 'background.default',
                      height: 90,
                      '&:hover': {
                        backgroundColor: button.id === 'buttonDone' && 'primary.main',
                      },
                    }}
                    fullWidth
                    disabled={buttonDisabled(button, inputFocusIndex, {
                      isScrapVehicle,
                      priceIsValid,
                      showWeightDecimals,
                      updateWeightPermission: hasEditPermission,
                    })}
                    onClick={() => {
                      onClickEntryButton(
                        button,
                        inputFocusIndex,
                        setInputFocusIndex,
                        materialFieldList,
                        onDone,
                        setValue,
                        hasEditPermission,
                        onClickAxleWeightsButton,
                        removeAxleWeights,
                      )
                      if (Number(button?.value)) {
                        const { fieldName } = materialFieldList.find(field => field.index === inputFocusIndex) || {}
                        switch (fieldName) {
                          case 'material.gross':
                            callRequestImages(materialGrossImages, 'material.grossImages')
                            break
                          case 'material.tare':
                            callRequestImages(materialTareImages, 'material.tareImages')
                            break
                          default:
                            break
                        }
                      }
                    }}
                  >
                    {button.content}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Stack>
      )}
    </FormProvider>
  )
}

MaterialEntryForm.propTypes = {
  selectedMaterial: PropTypes.object,
  materialEntryViewMode: PropTypes.string,
  updateMaterialEntryViewMode: PropTypes.func,
  updateMaterialRow: PropTypes.func,
  deleteMaterialRow: PropTypes.func,
  pricePrepend: PropTypes.string,
  updateTruckWeight: PropTypes.func,
  truckWeight: PropTypes.object,
  purchaseOrderData: PropTypes.object,
  onEditScrapVehicle: PropTypes.func,
  clearErrors: PropTypes.func,
  activeMaterialForAxleWeights: PropTypes.object,
  setActiveMaterialForAxleWeights: PropTypes.func.isRequired,
  materials: PropTypes.array,
}

MaterialEntryForm.defaultProps = {
  selectedMaterial: {},
  materialEntryViewMode: SCALE_MATERIAL_VIEW_OPTIONS.BUTTONS,
  updateMaterialEntryViewMode: () => {},
  updateMaterialRow: () => {},
  deleteMaterialRow: () => {},
  pricePrepend: '$',
  updateTruckWeight: () => {},
  truckWeight: {},
  purchaseOrderData: null,
  onEditScrapVehicle: () => {},
  clearErrors: () => {},
  activeMaterialForAxleWeights: null,
  materials: [],
}

export default MaterialEntryForm
